import { useLocation } from '@reach/router'
import * as React from 'react'
import ReactDOM from 'react-dom'
import styled, { css } from 'styled-components'
import { Colors } from '../constants'
import { TestIds } from '../data/testids'
import keyboard, { KeyAction } from '../keyboard'
import Button from './Button'
import Icon from './Icon'
import { H3 } from './core'

interface ModalProps {
  title: string
  width: number
  height?: number
  onClose?: () => void
  children: React.ReactNode
}

export default function Modal(props: ModalProps) {
  const onKey = (action: KeyAction) => {
    if (action === KeyAction.esc && props.onClose != null) {
      props.onClose()
    }
  }

  const location = useLocation()
  const [initLocation] = React.useState(location.href)

  React.useEffect(() => {
    keyboard.addListener(onKey)
    return () => {
      keyboard.removeListener(onKey)
    }
  }, [])

  React.useEffect(() => {
    if (initLocation !== location.href && initLocation + '#' !== location.href && props.onClose) {
      // location has changed; close modal
      props.onClose()
    }
  }, [location.href])

  const { title, width, height, children, onClose, ...rest } = props

  const ret = (
    <div>
      <ModalBackground />
      <ModalBoxContainer {...rest}>
        <ModalBox width={width}>
          <ModalHeader>
            <H3>
              {title}
              {onClose && (
                <ModalCloseButton size="small" color="steel" onClick={onClose} data-testid={TestIds.modalCloseButton}>
                  <Icon name="close" color="snow" />
                </ModalCloseButton>
              )}
            </H3>
          </ModalHeader>
          <ModalBody height={height}>{children}</ModalBody>
        </ModalBox>
      </ModalBoxContainer>
    </div>
  )
  const modalContainer = document.querySelector('#Modal-container')
  return ReactDOM.createPortal(ret, modalContainer!)
}

const ModalBackground = styled.div`
  z-index: 9;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.25);
`

const ModalBox = styled.div`
  ${({ width }: { width: number }) => css`
    position: fixed;
    width: ${width}px;
    background-color: ${Colors.ink};
    border: 1px solid ${Colors.steel};
    border-radius: 4px;
    overflow: hidden;
    box-shadow: black 0px 24px 48px;
  `};
`

const ModalBoxContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  padding: 32px;
  box-sizing: border-box;
`

const ModalHeader = styled.div`
  height: 28px;
  padding: 10px 24px;
  border-bottom: 1px solid ${Colors.steel};
`

const ModalCloseButton = styled(Button)`
  float: right;
  margin-top: -11px;
  margin-right: -25px;
  border-radius: 0 4px 0 0;
  height: 50px;
  width: 50px;
`

const ModalBody = styled.div`
  ${({ height }: { height?: number }) =>
    css`
      padding: 16px 24px;
      box-sizing: border-box;
      overflow-y: auto;
      overflow-x: hidden;
      height: ${height ? `${height}px` : 'auto'};
      max-height: 80vh;
    `}
`
